import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSavings, useContribute, useSetSchedule, useMyBanks, useRequestWithdrawal,useTarget } from "../../hooks/useSavings";
import Card from "../../components/ui/Card";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Badge from "../../components/ui/Badge";
import { usePaystackPayment, PaystackButton } from 'react-paystack';
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "symbol", maximumFractionDigits: 0 }).format(n);
const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function SavingsPage() {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { data: data }          = useSavings()     as any;
  const contribute        = useContribute();
  const withdraw          = useRequestWithdrawal();
  const setSchedule       = useSetSchedule();  

  const [amount,    setAmount]    = useState("5000");
  const [cadence,   setCadence]   = useState("monthly");
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [feedback,  setFeedback]  = useState("");
  const [payType, setPayType] = useState("savings");
  const [repayAmount, setRepayAmount] = useState("0");
  const [acctType, setAccType] = useState("savings");  

  const {account = {}, contributions={},schedule={},withdrawals={}} = data || {};
  
  const charges = Number(account?.[0]?.charges ?? 0);
  const balance   = ((account?.[0]?.balance ?? 0) - charges)<0? 0 : Number((account?.[0]?.balance ?? 0) - charges);
  const locked    = Number(account?.[0]?.locked_amount ?? 0);
  //const available = (balance - (locked+charges))<0? 0 : (balance - (locked+charges));  
  const { data: mybanks = [], isFetching } = useMyBanks() as any;
  const { data: targetValues, isPulling } = useTarget(account?.[0]?.member_id || "") as any;
  
  const withdrawtxns: any[] = (data?.withdrawals ?? []);
  
  const account_savings = account?.[0]?.balance || 0;
  const account_investment = account?.[1]?.balance || 0;
  const account_targeted = account?.[2]?.balance || 0;
  const available = (Number(account_savings)+Number(account_targeted) - (charges + locked))<0? 0 : (Number(account_savings) - Number(charges + locked)) * 0.5 + Number(account_targeted);
  let availableContionally = Number((balance - (locked)) * 0.5);

  const GOAL = targetValues?.[0]?.target_amount ?? 1000000;
  const pct  = Math.min(100, Math.round((account_investment / GOAL) * 100));

  const [availableToWithdraw, setAvailableToWithdraw] = useState(availableContionally);
  
  const portfolio = Number(account_savings) + Number(account_investment) + Number(account_targeted);
  //const account_contribution = ((account?.[0]?.balance || 0) - charges)<0? 0 : ((account?.[0]?.balance || 0) - charges);
  const account_contribution = (portfolio - charges)<0? 0 : (portfolio - charges);

  const pendingWithdraw   = withdrawtxns.reduce((s, t) => s + Number(t.amount), 0);
  const pending = pendingWithdraw>0? `Withdrawable | Pending request ${fmt(pendingWithdraw)}` : "Withdrawable"

  const updateWithdawalBalance = (value: string) =>{
    let newAmount = 0;
    if (value === "savings"){
      newAmount = Number(account_savings * 0.5);
    }
    if (value === "targeted"){
      newAmount = Number(account_targeted);
    }
    setAvailableToWithdraw(newAmount);
  }

  const handleWithdrawalAccount = (value: string) =>{
    const newVal = value;
    setAccType(newVal);
    updateWithdawalBalance(value);
  };

  const handleContribute = async () => {
    try {
      let payAccount = "savings";
      if (payType==="savings") { payAccount = "savings"}
      if (payType==="investment") { payAccount = "investment"}
      if (payType==="targeted") { payAccount = "targeted"}
      await contribute.mutateAsync({ amount: Number(amount), cadence:cadence, accountType:payAccount });
      setFeedback("✅ Contribution processed successfully!");
      setTimeout(() => setFeedback(""), 3000);
    } catch (e: any) { setFeedback(`❌ ${e?.error ?? "Payment failed"}`); }
  };

  const handleWithdraw = async () => {
    try {
      if (acctType===""){alert('Please select where you want to withraw from.'); return;}
      const resp = await withdraw.mutateAsync({amount: Number(withdrawAmt),accountType:acctType });      
      setFeedback("✅ Withdrawal initiated");
      alert("✅ Withdrawal request initiated");
      setWithdrawAmt("");
      setTimeout(() => setFeedback(""), 3000);
    } catch (e: any) { setFeedback(`❌ ${e?.error ?? "Withdrawal request failed"+ e?.message}`); }
  };

  const handleSchedule = async () => {
    try {
      await setSchedule.mutateAsync({ amount: Number(amount), cadence });
      setFeedback("✅ Auto-contribution schedule updated!");
      setTimeout(() => setFeedback(""), 3000);
    } catch (e: any) { setFeedback(`❌ ${e?.error ?? "Failed"}`); }
  };

  // Paystack config object
  // Callback if transaction finishes successfully on the UI
  const onSuccess = async (reference: string) => {
    try {
      // Send reference.reference to your Node.js backend for verification
      let payAccount = "savings";
      if (payType==="savings") { payAccount = "savings"}
      if (payType==="investment") { payAccount = "investment"}
      if (payType==="targeted") { payAccount = "targeted"}
      const response = await api.post('/savings/contribute', {
        reference: reference,amount:amount, cadence:"one-off",repay:repayAmount,payType:payType,accountType:payAccount
      });
      
      if (response.data.success) {
        alert("Payment verified successfully on the server! Order complete.");
        setFeedback("✅ Contribution processed successfully!");
      } else {
        alert("Payment verification failed.");
      }
    } catch (error) {
      alert("Server error verifying payment.");
    }
  };

  // Callback if user closes the modal prematurely
  const onClose = () => {
    alert("Payment modal closed by user.");
  };
  
  const paystack_public_key = import.meta.env.VITE_PAYSTACK_KEY; 
  const ref = `REF_${new Date().getTime()}`;
  const config = {
    reference: ref, // Generate a unique reference
    email: user?.email || 'pay@heritageco-op.com',
    amount: payType==="both"? ((Number(amount)+Number(repayAmount)) * 100) : (Number(amount)*100), // Paystack accepts amounts in KOBO/lowest currency subunit (5000 * 100)
    publicKey: paystack_public_key, 
    onSuccess: () =>{onSuccess(ref)}, 
    onClose,
  };

  return (
    <div>
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard label="Total Value"  value={fmt(account_contribution)}    sub={`${pct}% of ${fmt(GOAL)} goal`} color="text-emerald-600" icon="💰"/>
        <StatCard label="Available"    value={fmt(available)}  sub={pending}                    color="text-blue-600"   icon="✅"/>
        <StatCard label="Locked"       value={fmt(locked)}     sub="Active surety obligations"       color="text-amber-600"  icon="🔒"/>
      </div>

      <Card className="mb-5">
        <p>Account Details</p>
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          {/* <div>
            <p>Contributions</p>
            <p className="text-xl font-medium text-emerald-600 mb-2">{fmt(balance)}</p>
          </div> */}
          <div>
            <p>Savings</p>
            <p className="text-lg md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_savings)}</p>
          </div>
          <div>
            <p>Investment</p>
            <p className="text-lg md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_investment)}</p>
          </div>          
          <div>
            <p>Targeted Savings</p>
            <p className="text-lg md:text-2xl font-medium mt-1 mb-1.5 md:mt-1.5 md:mb-0.5 text-emerald-600">{fmt(account_targeted)}</p>
          </div>
        </div>
      </Card>

      {/* Progress bar */}
      <Card className="mb-5">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-slate-700">Goal progress</span>
          <span className="text-emerald-600 font-medium">{fmt(balance)} / {fmt(GOAL)}</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}/>
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          <span>NGN 0</span>
          <span>{pct}% complete</span>
          <span>{fmt(GOAL)}</span>
        </div>
      </Card>

      {feedback && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${feedback.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {feedback}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Contribution setup */}
        <Card style={{backgroundColor:"#d8f9d8"}}>
          <p className="text-sm font-medium text-slate-700 mb-4">Make a contribution / Re-pay loan</p>
          <Select label="Payment type" value={payType} onChange={setPayType} options={[
            {value:"", label:"Select payment type"},
            {value:"savings", label:"Savings"},
            {value:"loan-repayment",label:"Loan repayment"},
            {value:"both",label:"Contribute and repay loan"},
            {value:"investment", label:"Investment"},            
            {value:"targeted", label:"Targeted Savings"}]} />
          {payType ==='targeted'? <em className="text-sm md:text-sm font-small mt-1 text-amber-600">{targetValues?.length > 0? "Purpose of saving:" : ""} {targetValues?.[0]?.purpose}</em> : ""}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" style={{width:"100%"}}>
            <div>
              <Input label="Amount (NGN)" type="number" value={amount} onChange={setAmount} placeholder="5000" />
            </div>            
            <div>
              {
                payType ==='both'? <Input label="Loan repay amount (NGN)" type="number" value={repayAmount} onChange={setRepayAmount} placeholder="500" /> :
                payType ==='targeted'? 
                <>
                  {isPulling? <Button variant="amber" size="sm" onClick={() => navigate(`/settarget/${account?.[0]?.member_id}`)}>Set Target</Button> 
                  : <span>{targetValues?.length > 0? <em className="text-sm md:text-sm font-small mt-1 text-amber-600">Target: { fmt(targetValues?.[0]?.target_amount)}</em> 
                  : <Button variant="amber" size="sm" onClick={() => navigate(`/settarget/${account?.[0]?.member_id}`)}>Set Target</Button>}
                    </span>
                  }
                </>
                : <span></span>
              }
            </div>
          </div>
          <Select label="Cadence" value={cadence} onChange={setCadence} options={[
            { value:"daily",   label:"Daily"   },
            { value:"weekly",  label:"Weekly"  },
            { value:"monthly", label:"Monthly" },
          ]}/>
          {/* Mock payment card display */}
          {/* <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Make Payment using paystack</p>
            <div className="flex gap-2">
              {["💳 Visa ····4242","💳 M/C ····5555"].map(c => (
                <div key={c} className="flex-1 border border-slate-200 rounded-md py-1.5 px-2.5 text-xs text-slate-600 bg-white">{c}</div>
              ))}
            </div>
          </div> */}
          <div className="flex gap-2">
            {/* <Button onClick={commitPayment} className="flex-1 justify-center" disabled={contribute.isPending}>
              {contribute.isPending ? "Processing…" : "▶ Pay now"}
            </Button> */}
            
            <PaystackButton {...config} className="paystack-button" disabled={contribute.isPending}>
              <img src="/paystacklogo.jpg" width="40" height="20" style={{borderRadius:"7px"}} alt="paystack" />
              {contribute.isPending ? "Processing…" : "Pay with Paystack now"}
            </PaystackButton>
            <Button onClick={handleSchedule} variant="ghost" className="flex-1 justify-center" disabled={setSchedule.isPending}>
              {setSchedule.isPending ? "Saving…" : "Set schedule"}
            </Button>
          </div>
          {schedule && (
            <div className="mt-3 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-700">
                ✅ Active schedule: {fmt(schedule?.[0]?.amount || 0)} / {schedule?.[0]?.cadence} · Next: {fmtDate(schedule?.[0]?.next_run_at)}
              </p>
            </div>
          )}
        </Card>

        {/* Withdrawal */}
        <Card style={{backgroundColor:"#f7e9cf"}}>
          <p className="text-sm font-medium text-slate-700 mb-4">Request Withdrawal</p>
          <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-200 space-y-1.5" style={{backgroundColor:"#f7e9cf"}}>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Total balance</span>
              <span className="font-medium text-slate-800">{fmt(available+locked)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Surety lock</span>
              <span className="font-medium text-amber-600">- {fmt(locked)}</span>
            </div>
            <Select label="Withdraw From" value={acctType} onChange={e => handleWithdrawalAccount(e)} options={[
              {value:"savings",label:"Savings"},
              {value:"targeted",label:"Targeted Savings"},
            ]}/>
            <div className="flex justify-between text-xs border-t border-slate-200 pt-1.5">
              <span className="text-slate-600 font-medium">Available to withdraw</span>
              <span className="font-medium text-emerald-600">{fmt(availableToWithdraw)}</span>
            </div>

            <Input label="Withdrawal amount (NGN)" type="number" value={withdrawAmt} onChange={setWithdrawAmt}
              placeholder={`Max ${fmt(availableToWithdraw)}`}/>            
            {/* <Select label="Destination" value="bank1" onChange={() => {}} options={mybanks.map((a:any) => ({value:a.bank,label:"🏦"+a.bank})) || []} /> */}
            <div className="flex justify-between text-xs border-t border-slate-200 pt-1.5">
              <span className="text-slate-600 font-medium">Pay to:</span>
              {
                mybanks?.length < 1? <Button variant="ghost" size="sm" onClick={() => navigate("/profile")}>Please add bank account</Button> : 
                <span className="font-medium text-emerald-600">{mybanks?.[0]?.bank}.... {mybanks?.[0]?.account_no.toString().slice(-4)}</span>
              }
            </div>
          </div>
          
          <Button onClick={handleWithdraw} variant="ghost" className="w-full justify-center"
            disabled={!withdrawAmt || Number(withdrawAmt) > available || withdraw.isPending || mybanks?.length<1}>
            {withdraw.isPending ? "Processing…" : "Request Withdrawal →"}
          </Button>
        </Card>
      </div>

      {/* Contribution history */}
      <Card>
        <p className="text-sm font-medium text-slate-700 mb-4">Contribution history</p>
        {contributions.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No transactions yet</p>
        ) : (
          contributions.slice(0,5).map((c: any) => (
            <div key={c.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
              <div className="flex gap-3 items-center">
                <span className="text-lg">{c.type === "contribution" ? "💰" : c.type === "withdrawal" ? "📤" : "🔄"}</span>
                <div>
                  <p className="text-sm font-medium text-slate-700 capitalize">{c.type.replace("-", " ")}</p>
                  <p className="text-xs text-slate-400">{fmtDate(c.created_at)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${c.type === "withdrawal" ? "text-red-500" : "text-emerald-600"}`}>
                  {c.type === "withdrawal" ? "-" : "+"}{fmt(c.amount)}
                </p>
                <Badge label={c.status} color={c.status === "completed" ? "emerald" : c.status === "failed" ? "red" : "amber"}/>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
